import React from "react"
import { cn } from "@/lib/utils"

const Skeleton = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement> & { variant?: "default" | "text" | "circular" | "card" | "table-row" }>((
  { className, variant = "default", ...props }, ref
) => {
  const variants = {
    default: "animate-pulse bg-brand-blue-100",
    text: "animate-pulse bg-brand-blue-100 h-4 w-full rounded",
    circular: "animate-pulse rounded-full bg-brand-blue-100",
    card: "animate-shimmer bg-brand-blue-50 rounded-2xl border border-brand-blue-100/50",
    "table-row": "animate-pulse bg-brand-blue-50 h-12 w-full rounded-lg"
  }

  return (
    <div
      ref={ref}
      className={cn(variants[variant], className)}
      {...props}
    />
  )
})
Skeleton.displayName = "Skeleton"

function SkeletonGroup({ 
  className, 
  count = 3,
  gap = "gap-3",
  ...props 
}: React.HTMLAttributes<HTMLDivElement> & { count?: number; gap?: string }) {
  return (
    <div className={cn("flex flex-col", gap, className)} {...props}>
      {Array.from({ length: count }).map((_, i) => (
        <Skeleton key={i} variant={i % 2 === 0 ? "default" : "text"} className="w-full" />
      ))}
    </div>
  )
}

function SkeletonCard({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn("bg-white rounded-2xl border border-brand-blue-100 shadow-soft p-6 space-y-4", className)} {...props}>
      <div className="flex items-center gap-4">
        <Skeleton variant="circular" className="h-12 w-12" />
        <div className="space-y-2 flex-1">
          <Skeleton variant="text" className="w-3/4" />
          <Skeleton variant="text" className="w-1/2 h-3" />
        </div>
      </div>
      <Skeleton variant="text" className="w-full" />
      <Skeleton variant="text" className="w-5/6" />
      <div className="flex gap-2">
        <Skeleton className="h-10 flex-1 rounded-xl" />
        <Skeleton className="h-10 flex-1 rounded-xl" />
      </div>
    </div>
  )
}

function SkeletonTable({ 
  className, 
  rows = 5,
  columns = 4,
  ...props 
}: React.HTMLAttributes<HTMLDivElement> & { rows?: number; columns?: number }) {
  return (
    <div className={cn("bg-white rounded-2xl border border-brand-blue-100 overflow-hidden", className)} {...props}>
      <div className="bg-brand-blue-50/50 px-6 py-4 grid gap-4" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={i} variant="text" className="h-4" />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, rowIdx) => (
        <div key={rowIdx} className="px-6 py-4 border-t border-brand-blue-50 grid gap-4" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
          {Array.from({ length: columns }).map((_, colIdx) => (
            <Skeleton key={colIdx} variant="text" className={colIdx === 0 ? "w-3/4" : "w-full"} />
          ))}
        </div>
      ))}
    </div>
  )
}

export { Skeleton, SkeletonGroup, SkeletonCard, SkeletonTable }